// Related posts by shared tags. Falls back to most-recent posts if no tags match.
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function scoreOverlap(a = [], b = []) {
  if (!a.length || !b.length) return 0;
  const setB = new Set(b.map((t) => (t || "").toLowerCase()));
  return a.reduce((n, t) => n + (setB.has((t || "").toLowerCase()) ? 1 : 0), 0);
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso || ""; }
}

export default function RelatedPosts({ currentSlug, currentTags = [] }) {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/blog`);
        if (!cancel) setPosts(res.data.posts || []);
      } catch {
        /* silent */
      }
    })();
    return () => { cancel = true; };
  }, []);

  const related = useMemo(() => {
    const others = posts.filter((p) => p.slug !== currentSlug);
    const scored = others
      .map((p) => ({ post: p, score: scoreOverlap(currentTags, p.tags) }))
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        // Tie-breaker: newer first
        return (b.post.published_at || "").localeCompare(a.post.published_at || "");
      });
    return scored.slice(0, 3).map((s) => s.post);
  }, [posts, currentSlug, currentTags]);

  if (!related.length) return null;

  return (
    <section
      data-testid="related-posts"
      className="mt-16 pt-12 border-t border-[#D9CFA0]"
      aria-labelledby="related-heading"
    >
      <div className="flex items-baseline justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="overline-gold mb-2">Continue reading</div>
          <h2 id="related-heading" className="font-serif text-3xl md:text-4xl bb-ink font-light tracking-tight">
            Related from the journal
          </h2>
        </div>
        <Link
          to="/blog"
          data-testid="related-all-link"
          className="inline-flex items-center gap-2 text-sm bb-ink link-underline text-link-hover"
        >
          All articles <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {related.map((p) => (
          <Link
            key={p.slug}
            to={`/blog/${p.slug}`}
            data-testid={`related-post-${p.slug}`}
            className="group block bg-[#FAF6E8] border border-[#D9CFA0] rounded-lg overflow-hidden hover:shadow-md transition-shadow"
          >
            {p.hero_image && (
              <div className="aspect-[16/10] overflow-hidden bg-[#F2EBD3]">
                <img
                  src={p.hero_image}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            )}
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-wider text-[#0F1F4E]/60 mb-2 font-light">
                {fmtDate(p.published_at)} · {p.read_minutes} min read
              </div>
              <h3 className="font-serif text-xl bb-ink font-light leading-snug mb-2 group-hover:text-[#C9A65A] transition-colors">
                {p.title}
              </h3>
              <p className="text-sm bb-ink-soft font-light leading-relaxed line-clamp-3">
                {p.excerpt}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
